import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../lib/icons';

export type SelectOption = { value: string; label: string; disabled?: boolean };

/**
 * The app-wide dropdown control — replaces every native `<select>` so all
 * dropdowns share one look (the `.input` styling) and one behavior.
 *
 * - Button trigger shows the selected option's label (or a muted placeholder)
 *   with a chevron on the right.
 * - The listbox renders in a fixed-position portal anchored to the trigger
 *   rect, so it never gets clipped by `overflow` containers.
 * - Long lists get a **filter box** at the top (auto-enabled past a threshold,
 *   or force with `searchable`) so you can type instead of scrolling.
 * - Closes on outside click and Escape. Keyboard: Enter/Space/ArrowDown/ArrowUp
 *   open; arrows move the active option; Home/End jump; Enter/Space select. When
 *   the filter box is shown it takes focus and the arrows/Enter drive the list;
 *   otherwise printable characters type-ahead to a matching label.
 */
export function Select({
  value, onChange, options, placeholder, disabled, className = '', size = 'md',
  id, title, 'aria-label': ariaLabel, autoFocus, defaultOpen, searchable,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
  id?: string;
  title?: string;
  'aria-label'?: string;
  autoFocus?: boolean;
  /** Open the listbox immediately on mount (inline click-to-edit fields). */
  defaultOpen?: boolean;
  /** Force the filter box on/off. Defaults to on for lists longer than 7. */
  searchable?: boolean;
}) {
  const selected = options.find((o) => o.value === value);

  const [open, setOpen] = useState(defaultOpen ?? false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; up: boolean } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const typeahead = useRef<{ buf: string; at: number }>({ buf: '', at: 0 });
  const listboxId = useId();

  // Show the filter box for long lists (or when explicitly asked). Short lists
  // (priority, severity, …) stay a plain listbox so nothing changes for them.
  const showSearch = searchable ?? options.length > 7;
  const q = query.trim().toLowerCase();
  const shown = showSearch && q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  const firstEnabled = (list: SelectOption[]) => list.findIndex((o) => !o.disabled);
  const lastEnabled = (list: SelectOption[]) => {
    for (let i = list.length - 1; i >= 0; i--) if (!list[i].disabled) return i;
    return -1;
  };

  const openList = () => {
    if (disabled) return;
    setQuery('');
    const si = options.findIndex((o) => o.value === value);
    setActive(si >= 0 ? si : Math.max(firstEnabled(options), 0));
    setOpen(true);
  };
  const close = () => setOpen(false);

  const commit = (i: number) => {
    const opt = shown[i];
    if (!opt || opt.disabled) return;
    close();
    if (opt.value !== value) onChange(opt.value);
  };

  /** Anchor the fixed-position listbox to the trigger; flip up when cramped. */
  const measure = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const maxH = 16 * 16; // matches max-h-64
    const below = window.innerHeight - r.bottom;
    const up = below < Math.min(maxH, 200) && r.top > below;
    setPos({ top: up ? r.top - 4 : r.bottom + 4, left: r.left, width: r.width, up });
  };

  useLayoutEffect(() => { if (open) measure(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (autoFocus) btnRef.current?.focus(); }, [autoFocus]);

  // Reset the highlight to the first match whenever the filter text changes.
  useEffect(() => { if (open && showSearch) setActive(Math.max(firstEnabled(shown), 0)); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      close();
    };
    const onMove = () => measure();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the active option visible while navigating with the keyboard.
  useEffect(() => {
    if (!open) return;
    popRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const step = (from: number, dir: 1 | -1): number => {
    let i = from;
    for (let n = 0; n < shown.length; n++) {
      i += dir;
      if (i < 0 || i >= shown.length) break;
      if (!shown[i]?.disabled) return i;
    }
    return from;
  };

  const onTypeahead = (ch: string) => {
    const now = Date.now();
    const ta = typeahead.current;
    ta.buf = now - ta.at > 700 ? ch : ta.buf + ch;
    ta.at = now;
    const needle = ta.buf.toLowerCase();
    const start = open ? active : Math.max(options.findIndex((o) => o.value === value), 0);
    for (let n = 1; n <= options.length; n++) {
      const i = (start + n) % options.length;
      const o = options[i];
      if (!o.disabled && o.label.toLowerCase().startsWith(needle)) {
        // Closed-list type-ahead: no filter is applied, so `shown === options`
        // and this index is valid for commit().
        if (open) setActive(i);
        else commit(i);
        return;
      }
    }
  };

  // Arrow/Enter/Escape navigation shared by the trigger (no filter) and the
  // filter input (when shown). Operates on the currently-visible `shown` list.
  const onNavKey = (e: React.KeyboardEvent): boolean => {
    switch (e.key) {
      case 'Escape': e.preventDefault(); e.stopPropagation(); close(); btnRef.current?.focus(); return true;
      case 'Tab': close(); return true;
      case 'Enter': case ' ': e.preventDefault(); commit(active); return true;
      case 'ArrowDown': e.preventDefault(); setActive((a) => step(a, 1)); return true;
      case 'ArrowUp': e.preventDefault(); setActive((a) => step(a, -1)); return true;
      case 'Home': e.preventDefault(); setActive(Math.max(firstEnabled(shown), 0)); return true;
      case 'End': e.preventDefault(); setActive(Math.max(lastEnabled(shown), 0)); return true;
      default: return false;
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openList();
      } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        onTypeahead(e.key);
      }
      return;
    }
    // Open: when the filter box is present it owns keystrokes; otherwise the
    // trigger handles nav + printable-char type-ahead.
    if (showSearch) return;
    if (onNavKey(e)) return;
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      onTypeahead(e.key);
    }
  };

  const sizeCls = size === 'sm' ? 'px-2 py-0.5 text-sm rounded-md' : 'px-3 py-1.5 text-sm rounded-lg';

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        id={id}
        title={title}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open && !showSearch ? `${listboxId}-${active}` : undefined}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
        className={`inline-flex items-center justify-between gap-1.5 text-left bg-surface border border-border outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand ${sizeCls} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      >
        <span className={`truncate ${selected ? '' : 'text-muted'}`}>{selected?.label ?? placeholder ?? 'Select…'}</span>
        <Icon name="chevron-down" className="shrink-0 text-muted text-[11px]" />
      </button>
      {open && pos && createPortal(
        <div
          ref={popRef}
          className="fixed z-[70] rounded-xl border border-border bg-surface shadow-lg text-sm flex flex-col"
          style={{
            top: pos.up ? undefined : pos.top,
            bottom: pos.up ? window.innerHeight - pos.top : undefined,
            left: pos.left,
            minWidth: pos.width,
            maxWidth: `min(90vw, ${Math.max(pos.width, 320)}px)`,
          }}
        >
          {showSearch && (
            <div className="p-1.5 border-b border-border">
              <input
                ref={searchRef}
                autoFocus
                className="input w-full !py-1 text-sm"
                placeholder="Filter…"
                value={query}
                aria-label="Filter options"
                aria-controls={listboxId}
                aria-activedescendant={`${listboxId}-${active}`}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onNavKey}
              />
            </div>
          )}
          <ul
            id={listboxId}
            role="listbox"
            aria-label={ariaLabel}
            className="max-h-64 overflow-auto p-1"
          >
            {shown.length === 0 && <li className="px-2.5 py-1.5 text-muted">No matches</li>}
            {shown.map((o, i) => (
              <li
                key={`${o.value}-${i}`}
                id={`${listboxId}-${i}`}
                data-index={i}
                role="option"
                aria-selected={o.value === value}
                aria-disabled={o.disabled || undefined}
                onMouseDown={(e) => e.preventDefault()} /* keep focus on the trigger/filter */
                onClick={() => commit(i)}
                onMouseMove={() => !o.disabled && setActive(i)}
                className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer ${
                  o.disabled ? 'text-muted opacity-50 cursor-not-allowed'
                    : i === active ? 'bg-surface2'
                      : ''
                } ${o.value === value ? 'font-medium text-brand' : ''}`}
              >
                <span className="truncate">{o.label}</span>
                {o.value === value && <Icon name="check" className="shrink-0 text-brand text-[11px]" />}
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
